import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import {
  ChatDataTable,
  ChatPagination,
  ChatPanel,
  ChatReportToolbar,
  ChatSummaryCards,
} from '../ChatUiComponents';

const ALIPAY_QR_IMAGE = `${process.env.PUBLIC_URL || ''}/images/QR.jpg`;
const ALIPAY_ALL_STORES = '__all__';
const BILLING_ALIPAY_UI = process.env.REACT_APP_BILLING_ALIPAY_UI !== 'false';
const BILLING_ALIPAY_PRECREATE = process.env.REACT_APP_BILLING_ALIPAY_PRECREATE === 'true';
const quickChartQrDataUrl = (text) =>
  `https://quickchart.io/qr?text=${encodeURIComponent(text)}&size=280&margin=2`;

function todayMinus(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `¥${value}`;
}

function parseBalance(value) {
  const n = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

export default function BalanceTopic({ storePk, storeExternalId, onOpenSupport }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [currentBalance, setCurrentBalance] = useState('');
  const [storesBalance, setStoresBalance] = useState([]);
  const [from, setFrom] = useState(todayMinus(90));
  const [to, setTo] = useState(todayMinus(0));
  const [topupRows, setTopupRows] = useState([]);
  const [topupTotal, setTopupTotal] = useState('0');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [alipayStorePk, setAlipayStorePk] = useState(ALIPAY_ALL_STORES);
  const [alipayAmount, setAlipayAmount] = useState('');
  const [alipayLoading, setAlipayLoading] = useState(false);
  const [alipayError, setAlipayError] = useState('');
  const [topupPlan, setTopupPlan] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [staticQrOpen, setStaticQrOpen] = useState(false);

  const loadBalance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getBillingExpenses({ page: 1, page_size: 1 });
      if (response?.current_balance != null) setCurrentBalance(response.current_balance);
      setStoresBalance(Array.isArray(response?.stores_balance) ? response.stores_balance : []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.chatUiReportsTopups({
        date_from: from,
        date_to: to,
        store_id: storeExternalId || undefined,
      });
      const d = res?.data ?? res;
      setTopupRows(d.rows || []);
      setTopupTotal(d.total || '0');
      setHistoryPage(1);
    } finally {
      setHistoryLoading(false);
    }
  }, [from, to, storeExternalId]);

  useEffect(() => {
    loadBalance();
    api.chatUiBillingTopupPlan().then((res) => setTopupPlan(res?.data ?? res)).catch(() => {});
  }, [loadBalance]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!storesBalance.length) {
      setAlipayStorePk('');
      return;
    }
    if (storePk && storesBalance.some((s) => String(s.store_id) === String(storePk))) {
      setAlipayStorePk(String(storePk));
      return;
    }
    if (
      alipayStorePk !== ALIPAY_ALL_STORES
      && !storesBalance.some((s) => String(s.store_id) === String(alipayStorePk))
    ) {
      setAlipayStorePk(ALIPAY_ALL_STORES);
    }
  }, [storesBalance, storePk, alipayStorePk]);

  const isAllStores = alipayStorePk === ALIPAY_ALL_STORES;

  const selectedStore = useMemo(() => {
    if (isAllStores) return null;
    return storesBalance.find((s) => String(s.store_id) === String(alipayStorePk)) || null;
  }, [storesBalance, alipayStorePk, isAllStores]);

  const selectedTopupPlan = useMemo(() => {
    if (isAllStores || !topupPlan?.stores?.length || !alipayStorePk) return null;
    return topupPlan.stores.find((s) => String(s.store_pk) === String(alipayStorePk)) || null;
  }, [topupPlan, alipayStorePk, isAllStores]);

  const allStoresPlanLines = useMemo(
    () => (Array.isArray(topupPlan?.stores) ? topupPlan.stores : []),
    [topupPlan],
  );

  useEffect(() => {
    if (isAllStores) {
      if (topupPlan?.total_topup) {
        setAlipayAmount(topupPlan.total_topup);
      }
      return;
    }
    if (selectedTopupPlan?.topup_amount) {
      setAlipayAmount(selectedTopupPlan.topup_amount);
    }
  }, [isAllStores, topupPlan, selectedTopupPlan]);

  const selectedDebt = useMemo(() => {
    const bal = parseBalance(selectedStore?.total_amount);
    return bal < 0 ? Math.abs(bal) : 0;
  }, [selectedStore]);

  const handleFillRecommended = () => {
    if (selectedTopupPlan?.topup_amount) {
      setAlipayAmount(selectedTopupPlan.topup_amount);
      return;
    }
    if (selectedDebt > 0) {
      setAlipayAmount(selectedDebt.toFixed(2));
    }
  };

  const handlePrimaryPay = async (newTab = false) => {
    if (isAllStores) {
      await handleCombinedPay(newTab);
      return;
    }
    await handlePagePay(newTab);
  };

  const handleCombinedPay = async (newTab = false) => {
    setAlipayError('');
    setAlipayLoading(true);
    try {
      const data = await api.chatUiBillingAlipayCombinedPagePay(
        isAllStores ? {} : (alipayStorePk ? { store_pk: alipayStorePk } : {}),
      );
      if (data?.pay_url) {
        if (newTab) {
          const opened = window.open(data.pay_url, '_blank', 'noopener,noreferrer');
          if (!opened) setAlipayError(t('billing.payWithAlipayPagePopupBlocked'));
        } else {
          window.location.assign(data.pay_url);
        }
      } else {
        setAlipayError(t('billing.payWithAlipayPageNoUrl'));
      }
    } catch (e) {
      const d = e?.data;
      setAlipayError(d?.error || d?.alipay?.sub_msg || e?.message || 'Request failed');
    } finally {
      setAlipayLoading(false);
    }
  };

  const filteredStoreBalance = useMemo(() => {
    if (!storePk) return storesBalance;
    return storesBalance.filter((s) => String(s.store_id) === String(storePk));
  }, [storesBalance, storePk]);

  const summaryItems = useMemo(() => {
    const items = [
      {
        key: 'total',
        value: formatMoney(currentBalance),
        label: t('chatApp.balanceTotal'),
      },
      {
        key: 'period',
        value: formatMoney(topupTotal),
        label: t('chatApp.topupsInPeriod'),
      },
    ];
    if (storePk && filteredStoreBalance[0]) {
      const sb = filteredStoreBalance[0];
      items.unshift({
        key: 'store',
        value: formatMoney(sb.total_amount),
        label: sb.store_name || t('chatApp.storeBalance'),
        variant: parseBalance(sb.total_amount) < 0 ? 'danger' : '',
      });
    }
    return items;
  }, [currentBalance, topupTotal, storePk, filteredStoreBalance, t]);

  const pagedRows = useMemo(() => {
    const start = (historyPage - 1) * pageSize;
    return topupRows.slice(start, start + pageSize);
  }, [topupRows, historyPage, pageSize]);

  const handlePagePay = async (newTab = false) => {
    setAlipayError('');
    if (!alipayStorePk) {
      setAlipayError(t('billing.payWithAlipayStoreRequired'));
      return;
    }
    const amountNum = parseFloat(String(alipayAmount).replace(',', '.').trim());
    if (Number.isNaN(amountNum) || amountNum < 0.01) {
      setAlipayError(t('billing.payWithAlipayAmountRequired'));
      return;
    }
    setAlipayLoading(true);
    try {
      const data = await api.createBillingAlipayPagePay(alipayStorePk, amountNum.toFixed(2));
      if (data?.pay_url) {
        if (newTab) {
          const opened = window.open(data.pay_url, '_blank', 'noopener,noreferrer');
          if (!opened) setAlipayError(t('billing.payWithAlipayPagePopupBlocked'));
        } else {
          window.location.assign(data.pay_url);
        }
      } else {
        setAlipayError(t('billing.payWithAlipayPageNoUrl'));
      }
    } catch (e) {
      const d = e?.data;
      setAlipayError(d?.error || d?.alipay?.sub_msg || e?.message || 'Request failed');
    } finally {
      setAlipayLoading(false);
    }
  };

  const handlePrecreateQr = async () => {
    if (!selectedStore) return;
    setAlipayError('');
    const amountNum = parseFloat(String(alipayAmount).replace(',', '.').trim());
    if (Number.isNaN(amountNum) || amountNum < 0.01) {
      setAlipayError(t('billing.payWithAlipayAmountRequired'));
      return;
    }
    setAlipayLoading(true);
    try {
      const data = await api.createBillingAlipayQr(selectedStore.store_id, amountNum.toFixed(2));
      setQrModal({ storeName: selectedStore.store_name, ...data });
    } catch (e) {
      const d = e?.data;
      setAlipayError(d?.alipay?.sub_msg || e?.message || 'Request failed');
    } finally {
      setAlipayLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="chat-app-balance">
      <ChatSummaryCards items={summaryItems} />

      <div className="chat-app-balance-pay-grid">
        <ChatPanel title={t('billing.payWithAlipay')}>
          {alipayError && (
            <Alert variant="danger" className="py-2 small mb-3" dismissible onClose={() => setAlipayError('')}>
              {alipayError}
            </Alert>
          )}
          {storesBalance.length === 0 ? (
            <p className="small text-muted mb-0">{t('billing.payWithAlipayNoStores')}</p>
          ) : (
            <div className="chat-app-pay-form">
              <Form.Group className="mb-2">
                <Form.Label className="chat-app-field-label">{t('billing.payWithAlipayStoreLabel')}</Form.Label>
                <Form.Select
                  size="sm"
                  value={alipayStorePk}
                  onChange={(e) => setAlipayStorePk(e.target.value)}
                  disabled={alipayLoading || Boolean(storePk)}
                >
                  {!storePk && (
                    <option value={ALIPAY_ALL_STORES}>
                      {t('billing.payWithAlipayAllStores')}
                    </option>
                  )}
                  {storesBalance.map((s) => (
                    <option key={s.store_id} value={String(s.store_id)}>
                      {s.store_name} ({s.total_amount} {t('common.currency.yen')})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="chat-app-field-label">{t('billing.payWithAlipayAmountLabel')}</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  min="0.01"
                  step="0.01"
                  value={alipayAmount}
                  onChange={(e) => setAlipayAmount(e.target.value)}
                  disabled={alipayLoading || isAllStores}
                  readOnly={isAllStores}
                  placeholder="0.00"
                />
                {isAllStores && allStoresPlanLines.length > 0 && (
                  <div className="chat-app-pay-breakdown mt-2">
                    <div className="small fw-semibold mb-1">{t('billing.payWithAlipayAllBreakdownTitle')}</div>
                    <ul className="small mb-2 ps-3">
                      {allStoresPlanLines.map((row) => (
                        <li key={row.store_pk}>
                          {t('billing.payWithAlipayAllBreakdownLine', {
                            store: row.store_name,
                            debt: row.debt,
                            deposit: row.deposit_part,
                            total: row.topup_amount,
                          })}
                        </li>
                      ))}
                    </ul>
                    <p className="small text-muted mb-0">
                      {t('billing.payWithAlipayDepositExplain', {
                        target: topupPlan?.deposit_target || '1000',
                        total: topupPlan?.total_topup || alipayAmount,
                      })}
                    </p>
                  </div>
                )}
                {!isAllStores && selectedTopupPlan && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1 chat-app-link-btn"
                    disabled={alipayLoading}
                    onClick={handleFillRecommended}
                  >
                    {t('billing.payWithAlipayFillRecommended', {
                      amount: selectedTopupPlan.topup_amount,
                      debt: selectedTopupPlan.debt,
                    })}
                  </Button>
                )}
                {selectedTopupPlan?.deposit_part && parseFloat(selectedTopupPlan.deposit_part) > 0 && (
                  <p className="small text-muted mb-0 mt-1">
                    {t('chatApp.depositPaymentNote', {
                      debt: selectedTopupPlan.debt,
                      deposit: selectedTopupPlan.deposit_part,
                      target: topupPlan?.deposit_target || '1000',
                    })}
                  </p>
                )}
                {!isAllStores && !selectedTopupPlan && selectedDebt > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1 chat-app-link-btn"
                    disabled={alipayLoading}
                    onClick={() => setAlipayAmount(selectedDebt.toFixed(2))}
                  >
                    {t('billing.payWithAlipayFillDebt', { amount: selectedDebt.toFixed(2) })}
                  </Button>
                )}
              </Form.Group>
              <div className="chat-app-btn-row">
                {BILLING_ALIPAY_UI && (
                  <>
                    <Button
                      size="sm"
                      className="btn-gradient"
                      disabled={alipayLoading || (isAllStores && !topupPlan?.needs_payment)}
                      onClick={() => handlePrimaryPay(false)}
                    >
                      {alipayLoading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        isAllStores
                          ? t('billing.payWithAlipayPageButtonAll', { amount: topupPlan?.total_topup || alipayAmount })
                          : t('billing.payWithAlipayPageButton')
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={alipayLoading || (isAllStores && !topupPlan?.needs_payment)}
                      onClick={() => handlePrimaryPay(true)}
                    >
                      {t('billing.payWithAlipayPageNewTab')}
                    </Button>
                    {!isAllStores && BILLING_ALIPAY_PRECREATE && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={alipayLoading}
                        onClick={handlePrecreateQr}
                      >
                        {t('billing.payWithAlipayButton')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </ChatPanel>

        <ChatPanel title={t('chatApp.staticQrTitle')}>
          <div className="chat-app-static-qr text-center">
            <img
              src={ALIPAY_QR_IMAGE}
              alt="Alipay"
              className="chat-app-qr-thumb"
              onClick={() => setStaticQrOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setStaticQrOpen(true);
              }}
            />
            {onOpenSupport && (
              <Button size="sm" variant="outline-secondary" onClick={() => onOpenSupport({ subject: 'Send receipt' })}>
                {t('billing.sendReceiptButton')}
              </Button>
            )}
          </div>
        </ChatPanel>
      </div>

      <ChatReportToolbar
        from={from}
        to={to}
        onFrom={setFrom}
        onTo={setTo}
        onLoad={loadHistory}
        loading={historyLoading}
      />

      <ChatPanel title={t('chatApp.topupHistory')}>
        <ChatDataTable
          columns={[
            { key: 'date', label: t('chatApp.date') },
            { key: 'store_external_id', label: t('chatApp.store') },
            { key: 'amount', label: t('chatApp.amount'), render: (r) => formatMoney(r.amount) },
            { key: 'comment', label: t('chatApp.comment') },
          ]}
          rows={pagedRows}
          getRowKey={(r) => r.id}
        />
        <ChatPagination
          page={historyPage}
          pageSize={pageSize}
          totalItems={topupRows.length}
          onPageChange={setHistoryPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setHistoryPage(1);
          }}
        />
      </ChatPanel>

      <Modal show={staticQrOpen} onHide={() => setStaticQrOpen(false)} centered size="lg">
        <Modal.Header closeButton />
        <Modal.Body className="text-center p-4">
          <img src={ALIPAY_QR_IMAGE} alt="Alipay" className="img-fluid" style={{ maxHeight: '70vh' }} />
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(qrModal)} onHide={() => setQrModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('billing.payWithAlipay')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {qrModal?.qr_code && (
            <img src={quickChartQrDataUrl(qrModal.qr_code)} alt="QR" className="img-fluid mb-3" />
          )}
          <p className="small text-muted mb-0">
            {qrModal?.storeName} · ¥{qrModal?.amount}
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
}
